import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, qs, type Paged } from '../../api';
import { ColorSwatch, ItemImage } from '../../components/ItemImage';
import type { MarketLotRow } from '../../market';
import { useSettled } from '../../lib/useSettled';
import { Input, Spinner, cn, number } from '../../ui';

/** Where is it, and how many? Search by part (number, name, EAN) or by bin. */
export function MobileFind() {
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'part' | 'bin'>('part');
  const term = useSettled(text.trim());
  const found = useQuery({
    queryKey: ['m-find', mode, term],
    enabled: term.length >= 1,
    queryFn: () => api<Paged<MarketLotRow>>(`/api/v1/lots${qs({ ...(mode === 'bin' ? { location: `${term}*` } : { q: term }), pageSize: 25, sort: 'itemNo', dir: 'asc' })}`),
    placeholderData: (prev) => prev,
  });
  return (
    <div className="space-y-3">
      <div className="flex rounded-lg bg-slate-200 p-1 text-sm font-medium" role="tablist">
        {(['part', 'bin'] as const).map((m) => (
          <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => setMode(m)} className={cn('min-h-10 flex-1 rounded-md', mode === m ? 'bg-white shadow-sm' : 'text-slate-600')}>
            {m === 'part' ? 'Part' : 'Bin'}
          </button>
        ))}
      </div>
      <Input
        aria-label={mode === 'part' ? 'Part number or name' : 'Bin code'}
        className="min-h-12 text-base"
        autoFocus
        autoComplete="off"
        autoCapitalize="none"
        placeholder={mode === 'part' ? 'Part number or name' : 'Bin, e.g. A1'}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {found.isFetching && !found.data && <Spinner />}
      {term && found.data && found.data.rows.length === 0 && <p className="text-sm text-slate-600">Nothing in stock matches.</p>}
      <ul className="space-y-2">
        {found.data?.rows.map((r) => (
          <li key={r.id}>
            <Link to={`/lots/${r.id}`} className="flex gap-3 rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200 active:bg-slate-100">
              <ItemImage className="size-16" itemType={r.itemType} itemNo={r.itemNo} colorId={r.colorId} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">
                  {r.itemNo} <span className="font-normal text-slate-600">{r.itemName}</span>
                </span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-slate-600">
                  <ColorSwatch rgb={r.colorRgb} name={r.colorName} />
                  <span>{r.condition === 'N' ? 'New' : 'Used'}</span>
                </span>
                <span className="mt-1 block text-lg font-semibold tabular-nums">
                  {number(r.qtyTotal)}
                  <span className="ml-2 text-sm font-normal text-slate-600">
                    {r.locations.length === 0 ? 'no bin' : r.locations.map((l) => `${l.code ?? 'unplaced'} ×${l.qty}`).join(', ')}
                  </span>
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
